import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import GalleryCard from "@/components/feature/GalleryCard";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { videos } from "@/mocks/videos";
import { galleries } from "@/mocks/galleries";
import { newsArticles } from "@/mocks/news";
import { communityTopics } from "@/mocks/topics";
import { formatDate, formatViews, matchesKeyword } from "@/utils/format";

type TabKey = "all" | "video" | "image" | "news" | "topic";

const tabs: Array<{ key: TabKey; label: string; icon: string }> = [
  { key: "all", label: "全部", icon: "ri-apps-2-line" },
  { key: "video", label: "视频", icon: "ri-play-circle-line" },
  { key: "image", label: "图片", icon: "ri-image-line" },
  { key: "news", label: "资讯", icon: "ri-newspaper-line" },
  { key: "topic", label: "社区话题", icon: "ri-chat-3-line" },
];

const hotKeywords = ["霓虹夜色", "独立制片", "黑白胶片", "隐私", "社区"];

export default function SearchPage() {
  usePageMeta({
    title: "搜索结果 - 成人网",
    description:
      "在成人网内搜索视频、图片、资讯与社区话题。支持关键词模糊匹配，并可按内容类型筛选查看结果。",
    keywords: "站内搜索,成人网搜索,视频搜索,图片搜索,资讯搜索",
  });

  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const keyword = (params.get("q") ?? "").trim();
  const [draft, setDraft] = useState(keyword);
  const [tab, setTab] = useState<TabKey>("all");
  const [visible, setVisible] = useState(6);

  useEffect(() => {
    setDraft(keyword);
    setVisible(6);
  }, [keyword]);

  useEffect(() => {
    setVisible(6);
  }, [tab]);

  const videoResults = useMemo(
    () =>
      keyword
        ? videos.filter((video) =>
            matchesKeyword(keyword, [video.title, video.category, video.description, video.tags]),
          )
        : [],
    [keyword],
  );

  const imageResults = useMemo(
    () =>
      keyword
        ? galleries.filter((gallery) =>
            matchesKeyword(keyword, [
              gallery.title,
              gallery.theme,
              gallery.photographer,
              gallery.description,
              gallery.tags,
            ]),
          )
        : [],
    [keyword],
  );

  const newsResults = useMemo(
    () =>
      keyword
        ? newsArticles.filter((article) =>
            matchesKeyword(keyword, [
              article.title,
              article.category,
              article.excerpt,
              article.tags,
            ]),
          )
        : [],
    [keyword],
  );

  const topicResults = useMemo(
    () =>
      keyword
        ? communityTopics.filter((topic) =>
            matchesKeyword(keyword, [topic.title, topic.category, topic.excerpt, topic.content]),
          )
        : [],
    [keyword],
  );

  const counts: Record<TabKey, number> = {
    all: videoResults.length + imageResults.length + newsResults.length + topicResults.length,
    video: videoResults.length,
    image: imageResults.length,
    news: newsResults.length,
    topic: topicResults.length,
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = draft.trim();
    setParams(next ? { q: next } : {});
  };

  const slice = <T,>(list: T[]) => list.slice(0, visible);
  const activeList =
    tab === "video"
      ? videoResults
      : tab === "image"
        ? imageResults
        : tab === "news"
          ? newsResults
          : tab === "topic"
            ? topicResults
            : [];
  const hasMore = tab !== "all" && activeList.length > visible;

  return (
    <div className="container-page py-7">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

      <div className="mb-6 border-b border-background-200 pb-6">
        <span className="text-[11px] uppercase tracking-[0.28em] text-accent-500">Search</span>
        <h1 className="mt-2 font-heading text-2xl text-foreground-950 md:text-[30px]">
          {keyword ? `“${keyword}” 的搜索结果` : "站内搜索"}
        </h1>
        <p className="mt-2 text-[13px] text-foreground-500">
          {keyword
            ? `共找到 ${counts.all} 条结果，覆盖视频、图集、资讯与社区话题。`
            : "输入关键词即可搜索全站内容，支持标题、分类与标签的模糊匹配。"}
        </p>

        <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-2 sm:flex-row">
          <div className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3.5 focus-within:border-accent-500/70">
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="试试：霓虹、黑白、隐私…"
              aria-label="搜索关键词"
              className="w-full min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
            />
            {draft ? (
              <button
                type="button"
                onClick={() => setDraft("")}
                aria-label="清空关键词"
                className="text-foreground-500 hover:text-foreground-900 cursor-pointer"
              >
                <i className="ri-close-circle-line"></i>
              </button>
            ) : null}
          </div>
          <button
            type="submit"
            className="rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </form>
      </div>

      {keyword ? (
        <>
          <div className="mb-6 flex flex-wrap items-center gap-2">
            {tabs.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11.5px] transition-colors cursor-pointer ${
                  tab === item.key
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-100 text-foreground-500 hover:text-foreground-900"
                }`}
              >
                <i className={item.icon}></i>
                {item.label}
                <span
                  className={
                    tab === item.key ? "text-background-50/80" : "text-foreground-600"
                  }
                >
                  {counts[item.key]}
                </span>
              </button>
            ))}
          </div>

          {counts.all === 0 ? (
            <div>
              <EmptyState
                icon="ri-search-eye-line"
                title="没有找到匹配的内容"
                description="可能是关键词太长，或者这部分内容还没有收录。换一个更短的关键词试试，也可以从热门入口开始浏览。"
                actionLabel="浏览全部视频"
                onAction={() => navigate("/video-category")}
              />
              <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="text-sm font-medium text-foreground-950">热门内容入口</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[
                    { label: "剧情视频", to: "/video-category?cat=剧情" },
                    { label: "黑白胶片图集", to: "/image-category" },
                    { label: "隐私保护资讯", to: "/news-detail/n1" },
                    { label: "社区热门话题", to: "/community" },
                  ].map((item) => (
                    <Link
                      key={item.label}
                      to={item.to}
                      className="rounded-full bg-background-200 px-3 py-1.5 text-[11px] text-foreground-600 transition-colors hover:text-accent-500"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-10">
              {(tab === "all" || tab === "video") && videoResults.length > 0 ? (
                <section>
                  <div className="mb-4 flex items-end justify-between">
                    <h2 className="text-base text-foreground-950">
                      视频 <span className="text-xs text-foreground-500">({videoResults.length})</span>
                    </h2>
                    {tab === "all" ? (
                      <button
                        type="button"
                        onClick={() => setTab("video")}
                        className="text-[11px] text-foreground-500 hover:text-accent-500 cursor-pointer"
                      >
                        查看全部
                      </button>
                    ) : null}
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {(tab === "all" ? videoResults.slice(0, 3) : slice(videoResults)).map((video) => (
                      <VideoCard key={video.id} video={video} />
                    ))}
                  </div>
                </section>
              ) : null}

              {(tab === "all" || tab === "image") && imageResults.length > 0 ? (
                <section>
                  <div className="mb-4 flex items-end justify-between">
                    <h2 className="text-base text-foreground-950">
                      图片 <span className="text-xs text-foreground-500">({imageResults.length})</span>
                    </h2>
                    {tab === "all" ? (
                      <button
                        type="button"
                        onClick={() => setTab("image")}
                        className="text-[11px] text-foreground-500 hover:text-accent-500 cursor-pointer"
                      >
                        查看全部
                      </button>
                    ) : null}
                  </div>
                  <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                    {(tab === "all" ? imageResults.slice(0, 4) : slice(imageResults)).map((gallery) => (
                      <GalleryCard key={gallery.id} gallery={gallery} />
                    ))}
                  </div>
                </section>
              ) : null}

              {(tab === "all" || tab === "news") && newsResults.length > 0 ? (
                <section>
                  <div className="mb-3 flex items-end justify-between">
                    <h2 className="text-base text-foreground-950">
                      资讯 <span className="text-xs text-foreground-500">({newsResults.length})</span>
                    </h2>
                    {tab === "all" ? (
                      <button
                        type="button"
                        onClick={() => setTab("news")}
                        className="text-[11px] text-foreground-500 hover:text-accent-500 cursor-pointer"
                      >
                        查看全部
                      </button>
                    ) : null}
                  </div>
                  <div className="panel divide-y divide-background-200 px-2">
                    {(tab === "all" ? newsResults.slice(0, 3) : slice(newsResults)).map((article) => (
                      <Link
                        key={article.id}
                        to={`/news-detail/${article.id}`}
                        className="group flex items-start gap-3 p-3 transition-colors hover:bg-background-200/40"
                      >
                        <div className="h-16 w-24 shrink-0 overflow-hidden rounded bg-background-200">
                          <img
                            src={article.cover}
                            alt={`${article.title} - 资讯配图`}
                            title={`${article.title} 成人资讯`}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-[13.5px] font-medium text-foreground-900 group-hover:text-accent-500">
                            {article.title}
                          </h3>
                          <p className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                            <span className="text-accent-600">{article.category}</span>
                            <span>{formatDate(article.publishDate)}</span>
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line"></i>
                              {formatViews(article.views)}
                            </span>
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              ) : null}

              {(tab === "all" || tab === "topic") && topicResults.length > 0 ? (
                <section>
                  <div className="mb-3 flex items-end justify-between">
                    <h2 className="text-base text-foreground-950">
                      社区话题{" "}
                      <span className="text-xs text-foreground-500">({topicResults.length})</span>
                    </h2>
                    {tab === "all" ? (
                      <button
                        type="button"
                        onClick={() => setTab("topic")}
                        className="text-[11px] text-foreground-500 hover:text-accent-500 cursor-pointer"
                      >
                        查看全部
                      </button>
                    ) : null}
                  </div>
                  <div className="panel divide-y divide-background-200 px-2">
                    {(tab === "all" ? topicResults.slice(0, 3) : slice(topicResults)).map((topic) => (
                      <Link
                        key={topic.id}
                        to={`/community-detail/${topic.id}`}
                        className="group flex items-start justify-between gap-3 p-3 transition-colors hover:bg-background-200/40"
                      >
                        <div className="min-w-0">
                          <h3 className="text-[13.5px] font-medium text-foreground-900 group-hover:text-accent-500">
                            {topic.title}
                          </h3>
                          <p className="mt-1 text-[11px] text-foreground-500">
                            {topic.author} · {topic.category} · {topic.replyCount} 回复
                          </p>
                        </div>
                        <i className="ri-arrow-right-s-line mt-1 text-foreground-600 group-hover:text-accent-500"></i>
                      </Link>
                    ))}
                  </div>
                </section>
              ) : null}

              {hasMore ? (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((prev) => prev + 6)}
                    className="flex items-center gap-2 rounded-md border border-background-300 px-6 py-2.5 text-sm text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-500 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                    加载更多结果
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </>
      ) : (
        <div className="rounded-lg border border-background-200 bg-background-100 p-7">
          <h2 className="text-base text-foreground-950">试试这些关键词</h2>
          <p className="mt-1.5 text-xs text-foreground-500">
            我们整理了站内的常见搜索词，点击即可查看结果。
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {hotKeywords.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setParams({ q: item })}
                className="rounded-full bg-background-200 px-3.5 py-1.5 text-[11.5px] text-foreground-600 transition-colors hover:bg-primary-500 hover:text-background-50 cursor-pointer"
              >
                {item}
              </button>
            ))}
          </div>
          <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: "视频分类", desc: "按类别筛选", to: "/video-category", icon: "ri-play-circle-line" },
              { label: "图片分类", desc: "瀑布流浏览", to: "/image-category", icon: "ri-image-line" },
              { label: "资讯分类", desc: "行业与健康", to: "/news-category", icon: "ri-newspaper-line" },
              { label: "社区话题", desc: "参与讨论", to: "/community", icon: "ri-chat-3-line" },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="group rounded-lg border border-background-200 bg-background-50 p-3.5 transition-colors hover:border-accent-500/60"
              >
                <i className={`${item.icon} text-lg text-accent-500`}></i>
                <p className="mt-2 text-xs text-foreground-900 group-hover:text-accent-500">
                  {item.label}
                </p>
                <p className="mt-0.5 text-[10px] text-foreground-500">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}